import React, {
  ReactElement,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react'
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import clsx from 'clsx'
import type Swiper from 'swiper'

import FontSize from '@config/theme/definitions/fontSize'

import Carousel from '@objects/carousel'
import Image from '@objects/image'
import Button from '@objects/button'

export type PictureGalleryProps = RvG.IReactDefaultProps &
  RvG.Contentful.IContentfulModulePictureGallery & {
    observer?: boolean
    observeParents?: boolean
    autoHeight?: boolean
  }

const heights = {
  xs: '221px',
  lg: '396px',
}

/** Fixed carousel peek image height (default layout uses heights.xs / heights.lg on the wrapper). */
const carouselSliderHeight = '500px'

const PEEK_SPV_MIN = 1.02
const PEEK_SPV_MAX = 1.95
const PEEK_ASPECT_FALLBACK = 16 / 9

function parseCssLengthToPx(value: string): number {
  const n = parseFloat(value)
  return Number.isFinite(n) && n > 0 ? n : 500
}

/**
 * With fixed image height and fluid.aspectRatio (width / height), treat each slide as
 * ~aspectRatio * imageHeightPx wide, then derive slidesPerView including spaceBetween
 * (Swiper: (W + s) / (slideWidth + s)).
 */
function peekSlidesPerViewFromLayout(
  containerWidthPx: number,
  aspectRatio: number,
  imageHeightPx: number,
  spaceBetween: number
): number {
  if (!(containerWidthPx > 0) || !(aspectRatio > 0) || !(imageHeightPx > 0)) {
    return 1.2
  }
  const slideWidthPx = aspectRatio * imageHeightPx
  const s = Math.max(0, spaceBetween)
  const raw = (containerWidthPx + s) / (slideWidthPx + s)
  const clamped = Math.min(PEEK_SPV_MAX, Math.max(PEEK_SPV_MIN, raw))
  return Math.round(clamped * 1000) / 1000
}

const navTopLg = 'calc(50% - 60px)'
const navTopS = 'calc(50% - 16px)'

export type StyleProps = {
  layout: RvG.Contentful.IContentfulModulePictureGallery['layout']
  autoHeight: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(10),
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: theme.container.lg,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: theme.palette.background.greyDark,
    opacity: 0,
    height: 0,
    zIndex: -1,
    transition: `opacity ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}`,
    '.carouselIsOpen &': {
      zIndex: theme.zIndex.modal,
      opacity: 0.8,
      height: '100%',
    },
  },
  carousel: ({ layout }) => ({
    position: 'relative',
    ...(layout === 'carousel'
      ? {
          '& .swiper-pagination': {
            position: 'relative',
            bottom: 'auto',
            left: 0,
            width: '100%',
            marginTop: theme.spacing(2),
            textAlign: 'center',
          },
        }
      : {}),
    '.carouselIsOpen &': {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '100%',
      maxWidth: theme.container.lg,
      zIndex: theme.zIndex.modal + 1,
      padding: theme.spacing(0, 4),
      [theme.breakpoints.down('lg')]: {
        '& .swiper-wrapper': {
          alignItems: 'center',
        },
      },
      ...(layout === 'carousel'
        ? {
            '& .swiper': {
              overflow: 'hidden',
            },
            '& .swiper-slide': {
              overflow: 'hidden',
              boxSizing: 'border-box',
              maxWidth: '100%',
            },
          }
        : {}),
    },
  }),
  slideInner: {
    display: 'flex',
    flexDirection: 'column',
  },
  imageAspect: ({ layout }) => ({
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.background.greyDark,
    paddingBottom: 0,
    height: 'auto',
    ...(layout === 'carousel'
      ? {
          border: `1px solid ${theme.palette.common.black}`,
          borderRadius: 8,
          boxSizing: 'border-box',
        }
      : {}),
    '.carouselIsOpen &': {
      ...(layout === 'carousel'
        ? {
            border: 'none',
            borderRadius: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
            paddingLeft: theme.spacing(0.5),
          }
        : {}),
      paddingBottom: 0,
      height: 'auto',
      overflow: 'visible',
      [theme.breakpoints.up('md')]: {
        maxHeight: '80vh',
      },
    },
  }),
  imageAspectInner: {
    position: 'relative',
    width: '100%',
    height: 'auto',
    '& .gatsby-image-wrapper': {
      width: '100%',
      height: 'auto',
    },
    '.carouselIsOpen &': {
      position: 'relative',
      height: 'auto',
      overflow: 'visible',
      [theme.breakpoints.up('md')]: {
        maxHeight: '80vh',
      },
    },
    '.carouselIsOpen & .gatsby-image-wrapper': {
      width: '100%',
      height: 'auto !important',
      [theme.breakpoints.up('md')]: {
        maxHeight: '80vh',
      },
    },
  },
  image: ({ layout, autoHeight }) => ({
    margin: 0,
    ...(layout === 'default'
      ? {
          '& .gatsby-image-wrapper': {
            height: autoHeight ? 'auto' : heights.xs,
            [theme.breakpoints.up('lg')]: {
              height: autoHeight ? 'auto' : heights.lg,
            },
            '.carouselIsOpen &': {
              height: 'auto',
              [theme.breakpoints.up('md')]: {
                height: 'auto',
                maxHeight: '80vh',
              },
            },
          },
        }
      : {
          '& .gatsby-image-wrapper': {
            [theme.breakpoints.up('lg')]: {
              height: carouselSliderHeight,
            },
          },
          '.carouselIsOpen & .gatsby-image-wrapper': {
            height: 'auto',
            [theme.breakpoints.up('md')]: {
              height: 'auto',
              maxHeight: '80vh',
            },
          },
        }),
  }),
  imageWrapper: {
    position: 'relative',
    backgroundColor: theme.palette.background.greyDark,
  },
  caption: ({ layout }) => ({
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
    opacity: 0,
    transition: `opacity 0s`,
    ...(layout === 'carousel'
      ? {
          display: 'none',
          [theme.breakpoints.up('lg')]: {
            display: 'flex',
          },
        }
      : {
          display: 'flex',
        }),
    '.carouselIsOpen &': {
      color: theme.palette.text.invert,
      ...(layout === 'carousel'
        ? {
            display: 'flex',
            width: '100%',
          }
        : {}),
    },
    '&.captionIsVisible': {
      transition: `opacity ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}`,
      opacity: 1,
    },
    '&.copyrightNoteIsVisible': {
      transition: `opacity ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}`,
      opacity: 1,
      justifyContent: 'space-between',
    },
  }),
  captionIndicator: {
    fontWeight: 'bold',
    width: '60%',
    [theme.breakpoints.up('lg')]: {
      width: '20%',
      order: 1,
    },
  },
  captionDescription: {
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      width: '60%',
      textAlign: 'center',
      order: 2,
    },
  },
  captionSource: {
    fontSize: FontSize.xs,
    width: '40%',
    textAlign: 'right',
    [theme.breakpoints.up('lg')]: {
      width: '20%',
      order: 3,
    },
  },
  navBtn: ({ layout, autoHeight }) => ({
    position: 'absolute',
    ...(layout === 'carousel'
      ? {
          top: navTopS,
          [theme.breakpoints.up('lg')]: {
            top: navTopLg,
          },
          '.carouselIsOpen &': {
            top: navTopLg,
          },
        }
      : {
          top: autoHeight ? 'calc(50% - 26px)' : `calc(${heights.xs} / 2)`,
          [theme.breakpoints.up('lg')]: {
            top: autoHeight ? 'calc(50% - 26px)' : `calc(${heights.lg} / 2)`,
          },
          '.carouselIsOpen &': {
            top: 'calc(50% - 17px)',
            [theme.breakpoints.up('lg')]: {
              top: 'calc(auto / 2)',
            },
          },
        }),
    zIndex: 1,
    transform: 'translateY(-50%)',
    width: '28px',
    height: '71px',
  }),
  navBtnPrev: ({ layout }) => ({
    left: 0,
    '.carouselIsOpen &': {
      left:
        layout === 'carousel'
          ? `calc(${theme.spacing(4)}px + ${theme.spacing(0.5)}px + 1px)`
          : theme.spacing(4),
    },
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
  }),
  navBtnNext: ({ layout }) => ({
    right: 0,
    '.carouselIsOpen &': {
      right:
        layout === 'carousel'
          ? `calc(${theme.spacing(4)}px + 2px)`
          : theme.spacing(4),
    },
    borderTopLeftRadius: theme.spacing(2),
    borderBottomLeftRadius: theme.spacing(2),
  }),
  scaleBtn: {
    position: 'absolute',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    width: theme.spacing(6),
    height: theme.spacing(6),
    borderRadius: theme.spacing(0.5),
    zIndex: 2,
    touchAction: 'manipulation',
    '.carouselIsOpen &': {
      zIndex: 4,
    },
  },
}))

type CaptionBlockProps = {
  classes: ReturnType<typeof useStyles>
  picture: RvG.Contentful.IImageRef
  captionVisible: boolean
  imageNumber: number
  totalPictures: number
}

function CaptionBlock({
  classes,
  picture,
  captionVisible,
  imageNumber,
  totalPictures,
}: CaptionBlockProps): ReactElement {
  return (
    <div
      className={clsx(classes.caption, {
        captionIsVisible: Boolean(picture.caption) && captionVisible,
        copyrightNoteIsVisible: !picture.caption && captionVisible,
      })}
    >
      <div className={classes.captionIndicator}>
        Bild {imageNumber} von {totalPictures}
      </div>
      {picture.copyrightNote && (
        <div className={classes.captionSource}>{picture.copyrightNote}</div>
      )}
      {picture.caption && (
        <div className={classes.captionDescription}>{picture.caption}</div>
      )}
    </div>
  )
}

export default function PictureGallery(
  props: PictureGalleryProps
): ReactElement {
  const {
    pictures,
    observer,
    observeParents,
    autoHeight,
    layout: layoutProp,
  } = props
  const layout = layoutProp
  const isCarousel = layout === 'carousel'

  const classes = useStyles({
    layout,
    autoHeight: isCarousel ? false : Boolean(autoHeight),
  })
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))
  const carouselRef = useRef<HTMLDivElement>(null)
  const carouselSlideIndexToResync = useRef<number | null>(null)
  const [swiper, setSwiper] = useState<Swiper | null>(null)
  const [overlay, setOverlay] = useState(false)
  const [captionVisible, setCaptionVisible] = useState(true)
  const [placeholderHeight, setPlaceholderHeight] = useState(0)
  const [carouselWidth, setCarouselWidth] = useState(0)
  const [activePeekAspect, setActivePeekAspect] =
    useState<number>(PEEK_ASPECT_FALLBACK)

  const validSlides = useMemo(() => {
    const list: { picture: RvG.Contentful.IImageRef; originalIndex: number }[] =
      []
    pictures.forEach((picture, originalIndex) => {
      if (picture.image?.fluid) {
        list.push({ picture, originalIndex })
      }
    })
    return list
  }, [pictures])

  useEffect(() => {
    const ar = validSlides[0]?.picture.image?.fluid?.aspectRatio
    if (typeof ar === 'number' && ar > 0) {
      setActivePeekAspect(ar)
    }
  }, [validSlides])

  const syncPeekAspectFromSwiper = useCallback(
    (s: Swiper) => {
      if (!isCarousel) return
      const slide = validSlides[s.realIndex]
      const ar = slide?.picture.image?.fluid?.aspectRatio
      if (typeof ar === 'number' && ar > 0) {
        setActivePeekAspect(ar)
      }
    },
    [isCarousel, validSlides]
  )

  const handleSwiper = useCallback(
    (s: Swiper) => {
      setSwiper(s)
      syncPeekAspectFromSwiper(s)
    },
    [syncPeekAspectFromSwiper]
  )

  /** Persist realIndex before toggling lightbox so loop + slidesPerView changes can snap back. */
  function rememberCarouselSlideForResync() {
    if (isCarousel && swiper) {
      carouselSlideIndexToResync.current = swiper.realIndex
    }
  }

  useLayoutEffect(() => {
    if (!isCarousel) return undefined
    const el = carouselRef.current
    if (!el) return undefined
    if (typeof ResizeObserver === 'undefined') {
      setCarouselWidth(el.getBoundingClientRect().width)
      return undefined
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width
      if (w && w > 0) setCarouselWidth(w)
    })
    ro.observe(el)
    const w0 = el.getBoundingClientRect().width
    if (w0 > 0) setCarouselWidth(w0)
    return () => ro.disconnect()
  }, [isCarousel])

  const swiperOptions = useMemo(() => {
    if (!isCarousel) {
      return { centeredSlides: true as const }
    }
    const simulateTouch = overlay || !isLarge
    if (overlay) {
      return {
        slidesPerView: 1,
        spaceBetween: 0,
        centeredSlides: true,
        simulateTouch,
      }
    }
    const spacePeek = isLarge ? 20 : 16
    const fixedPeekHeightPx = isLarge
      ? parseCssLengthToPx(carouselSliderHeight)
      : 0
    const peekSpv =
      carouselWidth > 0
        ? peekSlidesPerViewFromLayout(
            carouselWidth,
            activePeekAspect,
            fixedPeekHeightPx,
            spacePeek
          )
        : 1.2
    return {
      slidesPerView: peekSpv,
      spaceBetween: spacePeek,
      centeredSlides: false,
      simulateTouch,
    }
  }, [isCarousel, overlay, isLarge, carouselWidth, activePeekAspect])

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      exitOverlay()
    }
  }

  function exitOverlay() {
    rememberCarouselSlideForResync()
    setOverlay(false)
    setTimeout(() => {
      if (!isCarousel) swiper?.update()
    }, 10)
  }

  function toggleOverlay() {
    const el = carouselRef.current
    if (!el) return
    rememberCarouselSlideForResync()
    const { height } = el.getBoundingClientRect()
    setPlaceholderHeight(!overlay ? height : 0)
    setOverlay(!overlay)
    setTimeout(() => {
      if (!isCarousel) swiper?.update()
    }, 10)
  }

  function stopSwiperPointer(e: React.SyntheticEvent) {
    e.stopPropagation()
  }

  function handleScaleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    toggleOverlay()
  }

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (swiper && typeof swiper.allowTouchMove === 'boolean') {
      swiper.allowTouchMove = isCarousel ? overlay || !isLarge : !isLarge
    }
  }, [isLarge, swiper, overlay, isCarousel])

  useEffect(() => {
    const htmlEl = document.getElementsByTagName('html')[0]
    const bodyEl = document.getElementsByTagName('body')[0]
    if (!overlay) {
      setPlaceholderHeight(0)
      htmlEl.style.overflow = ''
      bodyEl.style.overflow = ''
    } else {
      htmlEl.style.overflow = 'hidden'
      bodyEl.style.overflow = 'hidden'
    }
  }, [overlay])

  /**
   * Carousel only: after overlay toggles, Swiper loop geometry must match the preserved
   * logical slide (read ref inside rAF so Strict Mode does not clear it before the run).
   */
  useEffect(() => {
    if (!isCarousel || !swiper) return undefined
    let cancelled = false
    let innerRaf = 0
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        if (cancelled) return
        const preserved = carouselSlideIndexToResync.current
        if (preserved === null) return
        carouselSlideIndexToResync.current = null
        swiper.update()
        swiper.slideToLoop(preserved, 0, false)
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(outerRaf)
      cancelAnimationFrame(innerRaf)
    }
  }, [overlay, isCarousel, swiper])

  useLayoutEffect(() => {
    if (!isCarousel || overlay || !swiper) return undefined
    let cancelled = false
    const run = () => {
      if (!cancelled) swiper.update()
    }
    let innerRaf = 0
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(run)
    })
    const timeoutId = window.setTimeout(run, 100)
    return () => {
      cancelled = true
      cancelAnimationFrame(outerRaf)
      cancelAnimationFrame(innerRaf)
      window.clearTimeout(timeoutId)
    }
  }, [isCarousel, overlay, swiper, carouselWidth, activePeekAspect, isLarge])

  return (
    <section className={clsx(classes.root, { carouselIsOpen: overlay })}>
      <div className={classes.overlay} onClick={() => exitOverlay()} />
      <div ref={carouselRef} className={classes.carousel}>
        <Carousel
          onSwiper={handleSwiper}
          speed={600}
          loop
          onSlideChangeTransitionStart={() => setCaptionVisible(false)}
          onSlideChangeTransitionEnd={(s) => {
            setCaptionVisible(true)
            syncPeekAspectFromSwiper(s)
          }}
          autoHeight={isCarousel}
          observer={observer}
          observeParents={observeParents}
          {...swiperOptions}
          {...(isCarousel
            ? {
                pagination: overlay ? false : { clickable: true },
              }
            : {})}
        >
          {validSlides.map(({ picture, originalIndex }) => {
            const asset = picture.image
            const fluid = asset?.fluid
            if (!fluid) return null

            if (isCarousel) {
              return (
                <div
                  key={`picture-gallery-${originalIndex}`}
                  className={clsx(classes.slideInner, classes.carouselSlide)}
                >
                  <div className={classes.imageAspect}>
                    <div className={classes.imageAspectInner}>
                      <Image
                        className={classes.image}
                        image={fluid}
                        alt={asset?.description}
                        imgStyle={{
                          margin: '0',
                          objectFit: overlay
                            ? 'contain'
                            : isLarge
                            ? 'cover'
                            : 'contain',
                        }}
                      />
                      <Button
                        type="icon"
                        icon={overlay ? 'Descale' : 'Scale'}
                        className={clsx(classes.scaleBtn, 'swiper-no-swiping')}
                        onClick={handleScaleClick}
                        onPointerDown={stopSwiperPointer}
                        onMouseDown={stopSwiperPointer}
                        onTouchStart={stopSwiperPointer}
                        invert
                      />
                    </div>
                  </div>
                  <CaptionBlock
                    classes={classes}
                    picture={picture}
                    captionVisible={captionVisible}
                    imageNumber={originalIndex + 1}
                    totalPictures={pictures.length}
                  />
                </div>
              )
            }

            return (
              <React.Fragment key={`picture-gallery-${originalIndex}`}>
                <div className={classes.imageWrapper}>
                  <Image
                    className={classes.image}
                    image={fluid}
                    alt={asset?.description}
                    imgStyle={{
                      margin: '0',
                      objectFit: 'contain',
                    }}
                  />
                  <Button
                    type="icon"
                    icon={overlay ? 'Descale' : 'Scale'}
                    className={classes.scaleBtn}
                    onClick={toggleOverlay}
                    invert
                  />
                </div>
                <CaptionBlock
                  classes={classes}
                  picture={picture}
                  captionVisible={captionVisible}
                  imageNumber={originalIndex + 1}
                  totalPictures={pictures.length}
                />
              </React.Fragment>
            )
          })}
        </Carousel>
        <Button
          type="icon"
          icon="ChevronLeft"
          className={clsx(classes.navBtn, classes.navBtnPrev)}
          onClick={() => swiper?.slidePrev()}
        />
        <Button
          type="icon"
          icon="ChevronRight"
          className={clsx(classes.navBtn, classes.navBtnNext)}
          onClick={() => swiper?.slideNext()}
        />
      </div>
      <div style={{ height: placeholderHeight }} />
    </section>
  )
}
